import React, { Component } from 'react';
import Modal from 'react-modal';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { getStyle, getModalStyle, getCsrfToken, TYPE_CARE_SERVICE } from '../../../utils/utils';
import CBMediaQuery from '../../shared/CBMediaQuery';
import CBButtonSubmit from '../../shared/CBButtonSubmit';
import CBInputField from '../../shared/form_components/CBInputField';
import {
  getHetuValidator,
  getLengthValidator,
  getRegistrationNumberValidator,
} from '../../../utils/validators';
import {
  ASSET_TYPE_EQUIPMENT,
  REGISTRATION_COUNTRY_FI,
  REGISTRATION_COUNTRY_UNREGISTERED,
  getRegistrationCountryOptions,
} from '../../../utils/equipment';
import CBSelectField from '../../shared/form_components/CBSelectField';
import RadioButtonGroup from '../../shared/RadioButtonGroup';
import EquipmentCareServiceSearch from '../../shared/EquipmentCareServiceSearch';
import {
  requestVehiclePermission as _requestVehiclePermission,
} from '../../../actions/vehiclePermissionActions';
import {
  addCareServiceVehicle as _addCareServiceVehicle,
  requestGarageVehiclePermission as _requestGarageVehiclePermission,
  addGarageEquipment as _addGarageEquipment,
} from '../../../actions/vehicleActions';
import { fetchCareServices as _fetchCareServices } from '../../../actions/careServiceActions';
import { browserIsIE } from '../../../ie-support';
import CBButtonLink from '../../shared/CBButtonLink';
import { ListBox } from 'primereact/listbox';


Modal.setAppElement('#root');

const METER_HOURS = 'hours';
const METER_KM = 'km';

class AddCarModal extends Component {
  state = {
    formData: {
      registrationNumber: '',
      hetu: '',
      garage: null,
    },
    formValid: {
      registrationNumber: false,
      hetu: true,
    },
    addOrganizationCar: false,
    // 'FI' keeps the existing Traficom flow untouched; 'UNREGISTERED' switches
    // the form over to an equipment record.
    registrationCountry: REGISTRATION_COUNTRY_FI,
    // Deliberately short. Year model, dimensions, maintenance intervals,
    // readings and free-form data are all entered afterwards on the asset's
    // own page, where each already has its own dialog.
    equipment: {
      platform_number: '',
      manufacturer: '',
      model_name: '',
      // The ONE meter the record is followed by. Most unregistered machines
      // have an hour meter and no odometer, so hours is the starting point.
      meter: METER_HOURS,
    },
    // Care-service path, "Ei rek.": the customer's machine the search picked.
    careServiceVehicleId: null,
    // Fleet path, "Ei rek.": the organization's own huolenpitopalvelu the new
    // machine joins right away ('' = none). Suggested when there is only one.
    equipmentCareServiceId: '',
    equipmentCareServiceSuggested: false,
  };

  componentDidMount = () => {
    const { isOrganization, garages } = this.props;
    const { formData } = this.state;

    const newState = {
      addOrganizationCar: isOrganization,
    }

    if (this.props.defaultRegistrationCountry) {
      newState.registrationCountry = this.props.defaultRegistrationCountry;
    }

    if (garages && garages.length == 1) {
      newState.formData = {
        ...formData,
        garage: garages[0].id,
      }
    }

    this.setState(newState);

    const { organization, fetchCareServices } = this.props;
    if (garages && organization && organization.id) {
      fetchCareServices({ organization_id: organization.id });
    }
  };

  componentDidUpdate = () => {
    // The API middleware resolves .then() with undefined: suggest the single
    // care service once the list lands in the store.
    const { equipmentCareServiceSuggested } = this.state;
    const careServices = this.getOrganizationCareServices();
    if (!equipmentCareServiceSuggested && careServices.length) {
      this.setState({
        equipmentCareServiceSuggested: true,
        equipmentCareServiceId: careServices.length === 1 ? String(careServices[0].id) : '',
      });
    }
  };

  // Only this organization's own deals: the store may still hold another list.
  getOrganizationCareServices = () => {
    const { careServices, organization, garages } = this.props;
    if (!garages || !organization || !careServices) return [];
    return careServices.filter((careService) => (
      careService.organization && careService.organization.id == organization.id && careService.workshop
    ));
  };

  toggleAddOrganizationCar = () => {
    const { addOrganizationCar } = this.state;
    this.setState({ addOrganizationCar: !addOrganizationCar });
  };

  setIsValid = (name, isValid) => {
    const { formValid } = this.state;
    const newFormValid = {
      ...formValid,
      [name]: isValid,
    };

    this.setState({ formValid: newFormValid });
  };

  getIsValid = () => {
    const { formValid } = this.state;
    let isValid = true;
    Object.values(formValid).forEach(fieldIsValid => {
      if (!fieldIsValid) {
        isValid = false;
      }
    });
    return isValid;
  };

  updateFormState = changedObject => {
    const { formData } = this.state;
    const newFormData = Object.assign({}, formData);
    newFormData[changedObject.target] = changedObject.value;
    this.setState({ formData: newFormData });
  };

  isUnregistered = () => this.state.registrationCountry === REGISTRATION_COUNTRY_UNREGISTERED;

  updateRegistrationCountry = (changed) => {
    // The identifier and a plate are validated by different rules, so the
    // reg-number field's validity has to be re-decided when the country flips.
    const { formData, formValid } = this.state;
    this.setState({
      registrationCountry: changed.value,
      formValid: {
        ...formValid,
        registrationNumber: changed.value === REGISTRATION_COUNTRY_UNREGISTERED
          ? !!formData.registrationNumber
          : getRegistrationNumberValidator().validator((formData.registrationNumber || '').toUpperCase()),
      },
    });
  };

  updateEquipmentState = (changed) => {
    const { equipment } = this.state;
    this.setState({ equipment: { ...equipment, [changed.target]: changed.value } });
  };

  equipmentIsValid = () => {
    const { formData, equipment } = this.state;
    return !!(formData.registrationNumber || '').trim()
      && (formData.registrationNumber || '').trim().length <= 25
      && !!formData.garage;
  };

  submitEquipment = () => {
    const { addGarageEquipment, closeModal } = this.props;
    const { formData, equipment } = this.state;

    addGarageEquipment(formData.garage, {
      asset_type: ASSET_TYPE_EQUIPMENT,
      identifier: formData.registrationNumber.trim(),
      // Optional: a blank chassis number is sent as null.
      platform_number: (equipment.platform_number || '').trim() || null,
      manufacturer: equipment.manufacturer,
      model_name: equipment.model_name,
      tracks_km: equipment.meter === METER_KM,
      tracks_hours: equipment.meter === METER_HOURS,
      care_service: this.state.equipmentCareServiceId ? Number(this.state.equipmentCareServiceId) : null,
    });
    closeModal();
  };

  isConfirmedOrganization = () => {
    const {
      organization,
    } = this.props;

    return organization && organization.is_confirmed;
  };

  renderBuyButton = (hasMaxSubscriptions) => {
    if (this.getIsValid() && !hasMaxSubscriptions) {
      return (
        <button
          className='hover-green-border hover-green-text'
          id='checkout-button'
          type='submit'
        >
          Siirry maksamaan
        </button>
      );
    } else {
      return (
        <button
          id='checkout-button'
          type='submit'
          disabled
        >
          Siirry maksamaan
        </button>
      );
    }
  };

  render() {
    const { formData, addOrganizationCar, equipment } = this.state;
    const {
      modalIsOpen,
      closeModal,
      afterOpenModal,
      t,
      requestGarageVehiclePermission,
      requestVehiclePermission,
      garages,
      organization,
      user,
      listType,
      addCareServiceVehicle,
      selectedWorkshopId,
    } = this.props;

    const isCareService = listType === TYPE_CARE_SERVICE;
    // A workshop adds a customer's existing machine; it never creates one.
    const careServiceEquipment = isCareService && this.isUnregistered();

    const hasMaxSubscriptions = user.profile.active_organization_car_subscription_count >= 2;
    const maxTrialVehiclesReached = addOrganizationCar && organization && organization.is_trialing && !isCareService && organization.vehicle_count >= 3;

    return (
      <CBMediaQuery>
        {screenSize => (
          <Modal
            isOpen={modalIsOpen}
            onAfterOpen={afterOpenModal}
            onRequestClose={closeModal}
            style={getModalStyle(screenSize, styles.modal)}
            contentLabel='Add car modal'
          >
            <div style={getStyle(screenSize, styles, 'container')}>
              <p style={getStyle(screenSize, styles, 'title')}>
                {addOrganizationCar ? t('addOrganizationCarModalHeading') : t('addCarModalHeading')}
              </p>
              {
                garages && false && (
                  <div style={getStyle(screenSize, styles, 'addToGarageInfoContainer')}>
                    <p style={getStyle(screenSize, styles, 'info')}>{`${t('addingToGarageInfoText')}:`}</p>
                    <p style={getStyle(screenSize, styles, 'textGarageName')}></p>
                  </div>
                )
              }

              {
                garages && (
                  <div style={getStyle(screenSize, styles, 'garageListContainer')}>
                    <p style={getStyle(screenSize, styles, 'subTitle')}>
                      {t('Autotalli')}
                    </p>

                    <ListBox
                      filter
                      value={garages.find((gar) => gar.id === formData.garage)}
                      onChange={(e) => { this.updateFormState({ target: 'garage', value: e.value ? e.value.id : undefined }); }}
                      options={garages}
                      optionLabel='name'
                      className='w-full'
                      listStyle={{width: '400px'}}
                    />
                  </div>
                )
              }

              {
                // The fleet path (which passes `garages`) creates equipment;
                // the care-service path finds a customer's existing machine.
                // The my-portal path keeps the plain Traficom form.
                (garages || isCareService) && (
                  <div style={getStyle(screenSize, styles, 'selectContainer')}>
                    <CBSelectField
                      name='registrationCountry'
                      labelText={t('registrationCountryHeading')}
                      labelTextStyle={getStyle(screenSize, styles, 'selectLabel')}
                      inputStyle={getStyle(screenSize, styles, 'select')}
                      value={this.state.registrationCountry}
                      options={getRegistrationCountryOptions(t)}
                      onChange={this.updateRegistrationCountry}
                    />
                  </div>
                )
              }

              {
                careServiceEquipment && (
                  <EquipmentCareServiceSearch
                    candidatesUrl='/uiapi/1/care_service_vehicle/equipment_candidates/'
                    params={{ workshop_id: selectedWorkshopId }}
                    onSelect={(vehicleId) => this.setState({ careServiceVehicleId: vehicleId })}
                    inputStyle={browserIsIE() ? getStyle(screenSize, styles, 'inputIE') : getStyle(screenSize, styles, 'input')}
                    labelTextStyle={getStyle(screenSize, styles, 'fieldLabel')}
                  />
                )
              }

              {
                !careServiceEquipment && (
                  <p style={getStyle(screenSize, styles, 'subTitle')}>
                    {this.isUnregistered() ? t('equipmentIdentifierHeading') : t('addCarModalRegNumberHeading')}
                  </p>
                )
              }
              {
                careServiceEquipment ? null : this.isUnregistered() ? (
                  <CBInputField
                    name='registrationNumber'
                    inputStyle={browserIsIE() ? getStyle(screenSize, styles, 'inputIE') : getStyle(screenSize, styles, 'input')}
                    value={formData.registrationNumber}
                    placeholder={t('equipmentIdentifierPlaceholder')}
                    onChange={this.updateFormState}
                    validators={[getLengthValidator(1, 25)]}
                    isValidCallback={this.setIsValid}
                    disabled={addOrganizationCar && garages && !this.isConfirmedOrganization()}
                  />
                ) : (
                  <CBInputField
                    name='registrationNumber'
                    inputStyle={browserIsIE() ? getStyle(screenSize, styles, 'inputIE') : getStyle(screenSize, styles, 'input')}
                    value={formData.registrationNumber.toUpperCase()}
                    placeholder={t('regNroPlaceHolder')}
                    onChange={this.updateFormState}
                    validators={[getRegistrationNumberValidator()]}
                    isValidCallback={this.setIsValid}
                    disabled={addOrganizationCar && garages && !this.isConfirmedOrganization()}
                  />
                )
              }

              {
                this.isUnregistered() && !isCareService && (
                  <div style={getStyle(screenSize, styles, 'equipmentContainer')}>
                    <p style={getStyle(screenSize, styles, 'subTitle')}>
                      {t('equipmentChassisHeading')}
                    </p>
                    <CBInputField
                      name='platform_number'
                      inputStyle={browserIsIE() ? getStyle(screenSize, styles, 'inputIE') : getStyle(screenSize, styles, 'input')}
                      value={equipment.platform_number}
                      placeholder=''
                      onChange={this.updateEquipmentState}
                    />

                    <CBInputField
                      name='manufacturer'
                      labelText={t('equipmentManufacturerLabel')}
                      labelTextStyle={getStyle(screenSize, styles, 'fieldLabel')}
                      inputStyle={browserIsIE() ? getStyle(screenSize, styles, 'inputIE') : getStyle(screenSize, styles, 'input')}
                      value={equipment.manufacturer}
                      onChange={this.updateEquipmentState}
                    />
                    <CBInputField
                      name='model_name'
                      labelText={t('equipmentModelLabel')}
                      labelTextStyle={getStyle(screenSize, styles, 'fieldLabel')}
                      inputStyle={browserIsIE() ? getStyle(screenSize, styles, 'inputIE') : getStyle(screenSize, styles, 'input')}
                      value={equipment.model_name}
                      onChange={this.updateEquipmentState}
                    />

                    <RadioButtonGroup
                      name='equipmentMeter'
                      value={equipment.meter}
                      setValue={(value) => this.updateEquipmentState({ target: 'meter', value })}
                      options={[
                        { value: METER_HOURS, label: t('tracksHoursLabel') },
                        { value: METER_KM, label: t('tracksKmLabel') },
                      ]}
                    />

                    {
                      this.getOrganizationCareServices().length > 0 && (
                        <div style={getStyle(screenSize, styles, 'selectContainer')}>
                          <CBSelectField
                            name='equipmentCareService'
                            labelText={t('equipmentCareServiceLabel')}
                            labelTextStyle={getStyle(screenSize, styles, 'selectLabel')}
                            inputStyle={getStyle(screenSize, styles, 'select')}
                            value={this.state.equipmentCareServiceId}
                            options={[{ label: t('noCareServiceOption'), value: '' }].concat(
                              this.getOrganizationCareServices().map((careService) => (
                                { label: careService.workshop.name, value: String(careService.id) }
                              )),
                            )}
                            onChange={(changed) => this.setState({ equipmentCareServiceId: changed.value })}
                          />
                        </div>
                      )
                    }
                  </div>
                )
              }
              {
                addOrganizationCar && !garages && !isCareService && (
                  <div>
                    <p style={getStyle(screenSize, styles, 'subTitle')}>
                      {t('addCarModalHetuHeading')}
                    </p>

                    <CBInputField
                      name='hetu'
                      inputStyle={browserIsIE() ? getStyle(screenSize, styles, 'inputIE') : getStyle(screenSize, styles, 'input')}
                      value={formData.hetu.toUpperCase()}
                      placeholder='010170-000A'
                      onChange={this.updateFormState}
                      validators={[getHetuValidator()]}
                      isValidCallback={this.setIsValid}
                    />
                  </div>
                )
              }

              {
                !isCareService && !garages && (
                  <p style={getStyle(screenSize, styles, 'info')}>
                    {addOrganizationCar ? t('addOrganizationCarModalInfoText') : t('addCarModalInfoText')}
                  </p>
                )
              }

              {
                isCareService && (
                  <div>
                    <p style={getStyle(screenSize, styles, 'info')}>
                      {t('addCareServiceVehicleInfoText')}
                    </p>
                    <p style={getStyle(screenSize, styles, 'info')}>
                      {t('addCareServiceVehiclePriceText')}
                    </p>
                  </div>
                )
              }

              {
                addOrganizationCar && garages && !this.isConfirmedOrganization() && !isCareService && (
                  <div>
                    <p style={getStyle(screenSize, styles, 'error')}>
                      {t('organizationNotConfirmedText')}
                    </p>
                    <p style={getStyle(screenSize, styles, 'info')}>
                      {t('moveToConfirm')}<a href='/api/1/authentication/create-authentication-session/'>{t('here')}.</a>
                    </p>
                  </div>
                )
              }

              {
                !garages && !isCareService && (
                  <p style={getStyle(screenSize, styles, 'info')}>
                    {addOrganizationCar ? t('addCarIndividualInfoText') : t('addCarOrganizationInfoText')}
                    <CBButtonLink onClick={this.toggleAddOrganizationCar} text={`${t('here')}.`} />
                  </p>
                )
              }

              {
                !garages && addOrganizationCar && !isCareService && (
                  <p style={getStyle(screenSize, styles, 'info')}>
                    {t('addCarPriceInfo')}
                  </p>
                )
              }

              {
                maxTrialVehiclesReached && (
                  <p style={getStyle(screenSize, styles, 'error')}>
                    {t('maxTrialVehiclesReached')}
                  </p>
                )
              }

              {
                hasMaxSubscriptions && !garages && addOrganizationCar && !isCareService && (
                  <p style={getStyle(screenSize, styles, 'error')}>
                    {t('maxSubscriptionsReached')}
                  </p>
                )
              }

              <div style={getStyle(screenSize, styles, 'buttonContainer')}>
                <div style={getStyle(screenSize, styles, 'cancelButtonContainer')}>
                  <CBButtonSubmit
                    disabled={false}
                    onClick={() => {
                      closeModal();
                    }}
                    text={t('cancel')}
                    style={getStyle(screenSize, styles, 'submitButton')}
                  />
                </div>
                <div style={getStyle(screenSize, styles, 'submitButtonContainer')}>
                  {
                    !garages && addOrganizationCar && !isCareService && (
                      <form action='/api/1/payments/stripe-create-checkout-session/' method='POST'>
                        {/* Add a hidden field with the lookup_key of your Price */}
                        <input type='hidden' name='lookup_key' value='org_vehicle' />
                        <input type='hidden' name='hetu' value={formData.hetu} />
                        <input type='hidden' name='registrationNumber' value={formData.registrationNumber} />
                        <input
                          type='hidden'
                          value={getCsrfToken()}
                          name='csrfmiddlewaretoken'
                        />

                        { this.renderBuyButton(hasMaxSubscriptions) }

                      </form>
                    )
                  }
                  {
                    (garages || !addOrganizationCar || isCareService) && (
                      <CBButtonSubmit
                        disabled={
                          careServiceEquipment
                            ? !this.state.careServiceVehicleId
                            : (
                              this.isUnregistered()
                                ? !this.equipmentIsValid() || maxTrialVehiclesReached || !this.isConfirmedOrganization()
                                : !this.getIsValid() || (garages && !formData.garage) || maxTrialVehiclesReached
                            )
                        }
                        onClick={() => {
                          if (careServiceEquipment) {
                            addCareServiceVehicle('', selectedWorkshopId, {
                              registration_country: REGISTRATION_COUNTRY_UNREGISTERED,
                              vehicle_id: this.state.careServiceVehicleId,
                            });
                            closeModal();
                            return;
                          }
                          if (this.isUnregistered()) {
                            this.submitEquipment();
                            return;
                          }
                          if (garages) {
                            requestGarageVehiclePermission(
                              formData.registrationNumber,
                              formData.hetu,
                              formData.garage,
                            );
                          } else if (isCareService) {
                            addCareServiceVehicle(formData.registrationNumber, selectedWorkshopId);
                          } else {
                            requestVehiclePermission(
                              formData.registrationNumber,
                              formData.hetu,
                              null,
                            );
                          }
                          closeModal();
                        }}
                        text={t('addVehicle')}
                        style={getStyle(screenSize, styles, 'submitButton')}
                      />
                    )
                  }
                </div>
              </div>
            </div>
          </Modal>
        )}
      </CBMediaQuery>
    );
  }
}

const styles = {
  default: {
    container: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      alignItems: 'center',
      height: '100%',
    },
    garageListContainer: {
      marginBottom: 16,
    },
    equipmentContainer: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    selectContainer: {
      width: 400,
      maxWidth: '100%',
      marginBottom: 16,
    },
    selectLabel: {
      fontSize: 14,
      color: 'black',
      textAlign: 'center',
      fontWeight: 'bold',
    },
    select: {
      width: '100%',
      backgroundColor: '#ebebeb',
      color: 'black',
      border: 'none',
      borderRadius: 100,
      height: 40,
      fontSize: 14,
      textAlign: 'center',
    },
    fieldLabel: {
      fontSize: 14,
      color: 'black',
      textAlign: 'center',
      fontWeight: 'bold',
    },
    addToGarageInfoContainer: {
      marginBottom: 32,
      marginTop: 32,
    },
    submitButtonContainer: {
      width: 200,
      marginTop: 32,
    },
    cancelButtonContainer: {
      width: 200,
      marginTop: 32,
    },
    buttonContainer: {
      width: '80%',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    textGarageName: {
      fontSize: 18,
      color: '#0095da',
      fontFamily: 'TitilliumWeb-SemiBold',
      textAlign: 'center',
    },
    title: {
      fontSize: 32,
      color: '#0095da',
      textAlign: 'center',
    },
    subTitle: {
      fontSize: 14,
      color: 'black',
      textAlign: 'center',
      fontWeight: 'bold',
    },
    submitButton: {
      height: 50,
      width: '100%',
      border: '2px solid #0095da',
      color: '#0095da',
      borderRadius: 50,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
    },
    input: {
      backgroundColor: '#ebebeb',
      fontSize: 14,
      textAlign: 'center',
      width: 400,
      resize: 'none',
      color: 'black',
      borderRadius: 100,
      height: 40,
      border: 'none',
      marginBottom: 30,
    },
    inputIE: {
      backgroundColor: '#ebebeb',
      fontSize: 14,
      textAlign: 'center',
      width: 400,
      resize: 'none',
      color: 'black',
      borderRadius: 100,
      border: 'none',
      marginBottom: 30,
      padding: 8,
    },
    info: {
      width: '100%',
      fontSize: 14,
      color: 'black',
      paddingTop: 16,
      paddingHorizontal: 30,
      textAlign: 'center',
    },
    error: {
      width: '100%',
      fontSize: 14,
      color: 'red',
      paddingTop: 16,
      paddingHorizontal: 30,
      textAlign: 'center',
    },
  },
  small: {
    input: {
      width: '100%',
    },
  },
  modal: {
    default: {
      content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        width: 600,
        // Centred on the screen, so without a cap a tall form pushes its own
        // heading and buttons outside the viewport with no way to scroll.
        maxHeight: '95vh',
        overflowY: 'auto',
      },
      overlay: {
        zIndex: 100,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
      },
    },
    small: {
      content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        width: '90%',
        maxHeight: '95vh',
        overflowY: 'auto',
      },
      overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
      },
    },
  },
};

function mapStateToProps(state) {
  return {
    selectedWorkshopId: state.workshop.selectedWorkshop,
    careServices: state.careService.careServices,
  };
}

export default connect(
  mapStateToProps,
  {
    requestVehiclePermission: _requestVehiclePermission,
    requestGarageVehiclePermission: _requestGarageVehiclePermission,
    addCareServiceVehicle: _addCareServiceVehicle,
    addGarageEquipment: _addGarageEquipment,
    fetchCareServices: _fetchCareServices,
  },
)(translate('GarageView')(AddCarModal));
